import React, { useEffect, useState, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { Grid } from '@mui/material';
import Loading from "../../../components/Loading/Loading";
import { getFavoritosPaginated } from "../../../services/FavoritosServices";
import { ProductList } from '../../../components/ProductList/ProductList';
import ControlsRow from '../../../components/ControlsRows/ControlsRows';
import { pdf } from '@react-pdf/renderer';
import ExportPdf from '../../../components/pdf/exportPdf';
import { AuthContext } from "../../../context/authContext";
import logoDefault from "../../../images/productDefault.jpg";

const useQuery = () => new URLSearchParams(useLocation().search);

export const DatagridFavoritos = () => {
  const query = useQuery();
  const { empresa = {}, user } = useContext(AuthContext);
  const clienteSelected = JSON.parse(localStorage.getItem('clienteSelected'));
  const userId = clienteSelected ? clienteSelected.id : user?.id;
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(42);
  const [orderBy, setOrderBy] = useState('');
  const [generandoPDF, setGenerandoPDF] = useState(false);
  const [viewMode, setViewMode] = useState('horizontal');
  const [refreshFavoritos, setRefreshFavoritos] = useState(0); // Estado para forzar la recarga de favoritos

  const formatPrice = (value) =>
    "$" + Number(value).toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

  const getImageSrc = (producto) => {
    const imgArtPath = producto?.articulo.art_imagen?.trim();
    const imgEmpPath = empresa?.imagen_articulo?.trim();
    let imgPath;
    if (imgArtPath) {
      imgPath = `${process.env.REACT_APP_URL_BACKEND}${imgArtPath}`;
    } else if (imgEmpPath) {
      imgPath = `${process.env.REACT_APP_URL_BACKEND}${imgEmpPath}`;
    } else {
      imgPath = logoDefault;
    }

    return imgPath;
  };

  const getOrder = (orderBy) => {
    switch (orderBy) {
      case "precio_asc": return { orderBy: 'art_nprecioventa', orden: 'asc' };
      case "precio_desc": return { orderBy: 'art_nprecioventa', orden: 'desc' };
      case "nombre_asc": return { orderBy: 'art_descripcion', orden: 'asc' };
      case "nombre_desc": return { orderBy: 'art_descripcion', orden: 'desc' };
      case "codigo_asc": return { orderBy: 'art_codigo', orden: 'asc' };
      case "codigo_desc": return { orderBy: 'art_codigo', orden: 'desc' };
      default: return { orderBy: '', orden: '' };
    }
  };

  const getFavoritesByClient = async (userId, page, perPage, ordenamiento) => {
    setLoading(true);
    try {
      const res = await getFavoritosPaginated(page, Number(perPage), userId, ordenamiento.orderBy, ordenamiento.orden);
      if (res) {
        const updatedData = res?.favoritos?.map(
          producto => {
            const stockFormatted = Number.isInteger(producto?.articulo?.stock)
              ? producto?.articulo?.stock?.toString()
              : producto?.articulo?.stock?.toLocaleString('es-AR', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
                useGrouping: true
              });

            const hayOferta = producto?.articulo?.precioVigente !== producto?.articulo?.precioMostrar; 
            // Disponibilidad según venta_sin_stock y oferta
            let availability = producto?.articulo?.art_desclarga; // default

            let installment;

            if (empresa?.venta_sin_stock === false) {
              if (producto?.articulo?.stock > 0) {
                installment = hayOferta
                  ? `Oferta: ${stockFormatted} u`
                  : `Disponible: ${stockFormatted} u`;
              } else {
                installment = `Sin Stock`;
              }
            } else {
              installment = hayOferta ? "Oferta" : "Disponible";
            }


            return {
              id: producto.articulo.id,
              code: producto.articulo.art_codigo,
              category: producto?.articulo?.Ramo?.descripcion_ramo,
              description: producto.articulo.art_descripcion,
              section: "TrendingProducts",
              imgSrc: getImageSrc(producto),
              altText: producto.articulo.art_descripcion,
              title: producto.articulo.art_descripcion,
              price: "$" + producto.articulo?.precioMostrar?.toLocaleString('es-AR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }),
              priceOld: "$" + producto.articulo?.precioVigente?.toLocaleString('es-AR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }),
              percentage: (((producto.articulo?.precioVigente - producto.articulo?.precioMostrar) / producto.articulo?.precioVigente) * 100).toLocaleString('es-AR', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }),
              stockActual: stockFormatted,
              installment,
              availability,
              rating: null,
              reviews: 580,
            };
          }
        );

        setProducts(updatedData);
        setTotalPages(res.pagination.totalPage);
        setTotalItems(res.pagination.totalItems);
      }
    } catch (error) {
      console.error("Error al obtener favoritos:", error);
    }
    setLoading(false);
  };

  const fetchAllProductsForPdf = async () => {
    const ordenamiento = getOrder(orderBy);
    const previewResponse = await getFavoritosPaginated(1, 1, userId, ordenamiento.orderBy, ordenamiento.orden);
    const totalItems = previewResponse?.pagination?.totalItems;
    if (!totalItems) return [];

    const fullResponse = await getFavoritosPaginated(1, 1, userId, ordenamiento.orderBy, ordenamiento.orden);
    if (fullResponse?.favoritos) {
      return fullResponse.favoritos.map(producto => ({
        id: producto?.articulo?.id,
        code: producto?.articulo?.art_codigo,
        category: producto?.articulo?.Ramo?.descripcion_ramo,
        description: producto?.articulo?.art_descripcion,
        section: "TrendingProducts",
        imgSrc: getImageSrc(producto),
        altText: producto?.articulo?.art_descripcion || "Imagen del producto",
        title: producto?.articulo?.art_descripcion,
        price: formatPrice(producto?.articulo?.precioMostrar),
        installment: "Disponible",
        availability: producto?.articulo?.art_desclarga,
        rating: null,
        reviews: 580,
      }));
    }
    return [];
  };

  useEffect(() => {
    const ordenamiento = getOrder(orderBy);
    getFavoritesByClient(userId, currentPage, perPage, ordenamiento);
  }, [userId, currentPage, perPage, orderBy, empresa, refreshFavoritos]); // Dependemos de `refreshFavoritos`

  const handlePageChange = (event, value) => setCurrentPage(value);
  const handlePerPageChange = (event) => {
    setPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleOpenPdf = async () => {
    setGenerandoPDF(true);
    try {
      const allProducts = await fetchAllProductsForPdf();
      const blob = await pdf(<ExportPdf products={allProducts} empresa={empresa} seccion={"Favoritos"} />).toBlob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (err) {
      console.error("Error generando PDF:", err);
    } finally {
      setGenerandoPDF(false);
    }
  };

  // Funciones de agregar/quitar de favoritos
  const handleRefreshFavorito = () => {
    setRefreshFavoritos(prev => prev + 1); // Cambiar el estado para forzar la recarga de favoritos
  };

  if (!empresa || loading) {
    return <Loading message="Cargando Favoritos..." />;
  }

  return (
    <Grid container spacing={2} p={2} alignItems="center">
      <Grid container spacing={2} sx={{ mb: 2 }} justifyContent="center">
        <ControlsRow
          products={products}
          generandoPDF={generandoPDF}
          setGenerandoPDF={setGenerandoPDF}
          handleOpenPdf={handleOpenPdf}
          viewMode={viewMode}
          setViewMode={setViewMode}
          orderBy={orderBy}
          setOrderBy={setOrderBy}
        />
      </Grid>
      <ProductList
        products={products}
        isCheckStock={empresa?.venta_sin_stock}
        viewMode={viewMode}
        totalPages={totalPages}
        currentPage={currentPage}
        perPage={perPage}
        totalItems={totalItems}
        handlePageChange={handlePageChange}
        handlePerPageChange={handlePerPageChange}
        origin={origin}
        handleRefreshFavorito={handleRefreshFavorito}
      />
    </Grid>
  );
};

export default DatagridFavoritos;
